import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { ErrorRecord, Product } from "../../types";
import { SoecoImageList } from "./SoecoImageList";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import Cropper, { Area, Point } from "react-easy-crop";
import React from "react";
import { getCroppedImg, readFile } from "../../utils/util";
import { SoecoButton } from "../../components/SoecoButton";
import Div100vh from "react-div-100vh";
import { Button, Card, CardActions, Input, Typography, Divider as MuiDivider } from "@mui/material";
import { theme } from "../../theme";
import { Upload, Image as ImageIcon, Camera } from "lucide-react";
import im from "./pic_will_add_later.jpg";

export const useIsAndroid = (): boolean => {
    const [isAndroid, setIsAndroid] = useState(false);

    useEffect(() => {
        const userAgent = navigator.userAgent || navigator.vendor;
        if (/android/i.test(userAgent)) {
            setIsAndroid(true);
        }
    }, []);

    return isAndroid;
};

type Props = {
    images: File[];
    setImages: (images: File[]) => void;
    imageUrls: string[];
    setImageUrls: (imageUrls: string[]) => void;
    error: boolean;
    errors: ErrorRecord<Product>;
    defaultPicture: boolean;
    setDefaultPicture: (defaultPicture: boolean) => void;
};

export const CameraForm = (props: Props) => {
    const isAndroid = useIsAndroid();
    const [imageSrc, setImageSrc] = React.useState("");
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const rotation = 0;
    const [croppedAreaPixels, setCroppedAreaPixels] = useState({} as Area);
    const [croppedImage, setCroppedImage] = useState("");

    useEffect(() => {
        if (props.images == null || props.images.length < 0) return;
        const newImageUrls: string[] = [];
        Array.from(props.images).forEach((image: File) => {
            newImageUrls.push(URL.createObjectURL(image));
        });
        props.setImageUrls(newImageUrls);
    }, [props.images]);

    useEffect(() => {
        // iPhone images from an iPhone 14 Pro Max is still very large even with compression
        // What is the image format that is used by the iPhone?
        // Is smaller versions of the image created in the backend?
        const img = new Image();
        img.src = croppedImage;
        img.onload = () => {
            URL.revokeObjectURL(img.src);
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx && ctx.drawImage(img, 0, 0);
            canvas.toBlob(
                blob => {
                    props.setImages([...props.images, blob as File]);
                },
                "image/jpeg",
                0.7,
            );
        };
        setZoom(1);
        setCrop({ x: 0, y: 0 });
        setImageSrc("");
    }, [croppedImage]);

    const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const showCroppedImage = useCallback(() => {
        getCroppedImg(imageSrc, croppedAreaPixels, rotation)
            .then(croppedImage => {
                setCroppedImage(croppedImage);
            })
            .catch(e => {
                console.error(e);
            });
    }, [imageSrc, croppedAreaPixels, rotation]);

    const onClose = useCallback(() => {
        props.setDefaultPicture(false);
        setCroppedImage("");
        setImageSrc("");
    }, []);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file: File = e.target.files[0];
            readFile(file, (result: string) => {
                setImageSrc(result);
            });
        }
    };
    useEffect(() => {
        if (props.defaultPicture) {
            props.setImages([]);
            setImageSrc(im);
        } else setImageSrc("");
    }, [props.defaultPicture]);
    return (
        <CameraInputWrapper>
            {imageSrc != "" ? (
                <>
                    <CropContainerWrapper>
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={4 / 3}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    </CropContainerWrapper>
                    <ButtonWrapper>
                        <SoecoButton fullWidth size="large" color="warning" onClick={onClose}>
                            Cancel
                        </SoecoButton>
                        <SoecoButton fullWidth size="large" onClick={showCroppedImage}>
                            Save
                        </SoecoButton>
                    </ButtonWrapper>
                </>
            ) : (
                <>
                    {!props.defaultPicture ? (
                        <>
                            <Card variant="outlined" sx={sx}>
                                <StyledCardActions>
                                    <IconActionsWrapper>
                                        <Camera />
                                        <StyledLabel htmlFor="upload-image-button">
                                            <Typography
                                                fontFamily={"'Poppins', sans-serif"}
                                                fontSize={18}
                                                color="#8c8c8c"
                                                fontWeight={600}
                                            >
                                                Image
                                            </Typography>{" "}
                                            {/* Upload Imagee
                                    <CameraAltIcon color="primary" fontSize="large" /> */}
                                        </StyledLabel>
                                        <MuiDivider
                                            orientation="vertical"
                                            variant="middle"
                                            flexItem
                                        />
                                        <StyledInput
                                            type="file"
                                            id="upload-image-button"
                                            name="image"
                                            accept="image/* ;capture=camera"
                                            onChange={onFileChange}
                                        />
                                        <ImageIcon />
                                        <StyledLabel
                                            onClick={() => {
                                                props.setDefaultPicture(!props.defaultPicture);
                                            }}
                                        >
                                            <Typography
                                                fontFamily={"'Poppins', sans-serif"}
                                                fontSize={18}
                                                color="#8c8c8c"
                                                fontWeight={600}
                                            >
                                                Default image
                                            </Typography>{" "}
                                        </StyledLabel>{" "}
                                    </IconActionsWrapper>
                                </StyledCardActions>

                                {props.error ? <p>{props.errors.imageUrls}</p> : null}
                                <SoecoImageList
                                    images={props.images}
                                    setImages={props.setImages}
                                    imageUrls={props.imageUrls}
                                    setImageUrls={props.setImageUrls}
                                    error={props.error}
                                    errors={props.errors}
                                    defaultPicture={props.defaultPicture}
                                    setDefaultPicture={props.setDefaultPicture}
                                />
                            </Card>
                        </>
                    ) : (
                        <>
                            <Card variant="outlined" sx={sx}>
                                {" "}
                                <StyledCardActions>
                                    <IconActionsWrapper>
                                        <Camera />
                                        <StyledLabel htmlFor="upload-image-button">
                                            <Typography
                                                fontFamily={"'Poppins', sans-serif"}
                                                fontSize={18}
                                                color="#8c8c8c"
                                                fontWeight={600}
                                            >
                                                Upload Image
                                            </Typography>{" "}
                                            {/* Upload Imagee
                            <CameraAltIcon color="primary" fontSize="large" /> */}
                                        </StyledLabel>
                                        <MuiDivider
                                            orientation="vertical"
                                            variant="middle"
                                            flexItem
                                        />
                                        <StyledInput
                                            type="file"
                                            id="upload-image-button"
                                            name="image"
                                            accept="image/*"
                                            onChange={onFileChange}
                                        />
                                        <ImageIcon />
                                        <StyledLabel
                                            onClick={() => {
                                                props.setDefaultPicture(!props.defaultPicture);
                                            }}
                                        >
                                            <Typography
                                                fontFamily={"'Poppins', sans-serif"}
                                                fontSize={18}
                                                color="#8c8c8c"
                                                fontWeight={600}
                                            >
                                                Default image
                                            </Typography>{" "}
                                        </StyledLabel>{" "}
                                    </IconActionsWrapper>
                                </StyledCardActions>
                                <SoecoImageList
                                    images={props.images}
                                    setImages={props.setImages}
                                    imageUrls={props.imageUrls}
                                    setImageUrls={props.setImageUrls}
                                    error={props.error}
                                    errors={props.errors}
                                    defaultPicture={props.defaultPicture}
                                    setDefaultPicture={props.setDefaultPicture}
                                />
                            </Card>
                        </>
                    )}
                </>
            )}
        </CameraInputWrapper>
    );
};

const CameraInputWrapper = styled(Div100vh)`
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
`;

const ButtonWrapper = styled.div`
    position: absolute;
    display: flex;
    width: 95%;
    bottom: 0;
    gap: 10px;
    z-index: 1;
    margin-bottom: 52px;
    button {
        padding: 10px 16px;
    }
`;

const StyledInput = styled.input`
    display: none;
`;

const StyledLabel = styled.label`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
`;

const CropContainerWrapper = styled.div`
    display: flex;
`;
const IconActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    gap: 5px;
`;
const StyledCardActions = styled(CardActions)`
    display: flex;
    width: 100%;
    font-size: 3rem;
    border-bottom: 1px solid #ebebeb;
    //background-color: #ebebeb;
    align-items: center; /* Centrerar vertikalt i flex-container */
`;
const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
`;
const sx = {
    borderRadius: "30px",
    width: "90%",
    overflowY: "auto",
    maxHeight: "80vh",
    padding: "5px",
    marginBottom: "30px",
};
